import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { StatusTimeline } from '../components/tracking/StatusTimeline';
import { useOrderTracking } from '../lib/hooks';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { formatCurrency, getStatusColor } from '../lib/utils';
import { Search, MapPin, Store, Receipt, Phone, Copy, CheckCircle2 } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
export function TrackingPage() {
  const {
    trackingToken
  } = useParams<{
    trackingToken: string;
  }>();
  const {
    order,
    events,
    loading,
    error
  } = useOrderTracking(trackingToken);
  const [copied, setCopied] = useState(false);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  if (loading) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-1576">
        <MarketplaceHeader data-id="element-1577" />
        <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-12" data-id="element-1578">
          <Skeleton className="h-12 w-1/2 mb-8" data-id="element-1579" />
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-8" data-id="element-1580">
            <Skeleton className="h-8 w-1/3 mb-4" data-id="element-1581" />
            <Skeleton className="h-4 w-full mb-2" data-id="element-1582" />
            <Skeleton className="h-4 w-2/3" data-id="element-1583" />
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-1584">
            {[1, 2, 3, 4].map(i => <div key={i} className="flex gap-4 mb-6" data-id="element-1585">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" data-id="element-1586" />
                <div className="flex-1" data-id="element-1587">
                  <Skeleton className="h-6 w-1/3 mb-2" data-id="element-1588" />
                  <Skeleton className="h-4 w-full" data-id="element-1589" />
                </div>
              </div>)}
          </div>
        </main>
        <Footer data-id="element-1590" />
      </div>;
  }
  if (error || !order) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-1591">
        <MarketplaceHeader data-id="element-1592" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-1593">
          <EmptyState icon={Search} title="Pedido no encontrado" description="No pudimos encontrar un pedido con este enlace de seguimiento. Verifica que el enlace sea correcto." actionLabel="Volver al inicio" onAction={() => window.location.href = '/'} data-id="element-1594" />
        </main>
        <Footer data-id="element-1595" />
      </div>;
  }
  return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-1596">
      <MarketplaceHeader data-id="element-1597" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12" data-id="element-1598">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-1599">
          <div data-id="element-1600">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1601">
              Seguimiento de Pedido
            </h1>
            <div className="flex items-center gap-3" data-id="element-1602">
              <span className="text-gray-500 font-medium" data-id="element-1603">
                Orden #{order.order_number}
              </span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`} data-id="element-1604">
                {order.status === 'new' ? 'Recibido' : order.status === 'accepted' ? 'Aceptado' : order.status === 'preparing' ? 'Preparando' : order.status === 'ready' ? 'Listo' : order.status === 'out_for_delivery' ? 'En camino' : order.status === 'delivered' ? 'Entregado' : 'Cancelado'}
              </span>
            </div>
          </div>

          <button onClick={handleCopyLink} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm w-fit" data-id="element-1605">
            {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-1606" /> : <Copy className="w-4 h-4" data-id="element-1607" />}
            {copied ? 'Enlace copiado' : 'Compartir seguimiento'}
          </button>
        </div>

        {/* Order Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-1608">
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-1609">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-1610">
              <Store className="w-5 h-5 text-blue-600" data-id="element-1611" />
            </div>
            <div data-id="element-1612">
              <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-1613">
                Restaurante
              </h3>
              <p className="font-medium text-gray-900" data-id="element-1614">
                Pedido del restaurante
              </p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1615">
                {order.delivery_type === 'pickup' ? 'Para llevar' : order.delivery_type === 'delivery' ? 'Delivery' : 'Comer aquí'}
              </p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-1616">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0" data-id="element-1617">
              <Receipt className="w-5 h-5 text-emerald-600" data-id="element-1618" />
            </div>
            <div data-id="element-1619">
              <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-1620">
                Total a pagar
              </h3>
              <p className="font-bold text-xl text-gray-900" data-id="element-1621">
                {formatCurrency(order.total)}
              </p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-1622">
                Pago:{' '}
                {order.payment_method === 'sinpe' ? 'SINPE Móvil' : order.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}
              </p>
            </div>
          </div>
        </div>

        {/* Main Timeline */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-8" data-id="element-1623">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1624">
            Estado del Pedido
          </h2>
          <StatusTimeline currentStatus={order.status} events={events} data-id="element-1625" />
        </div>

        {/* Delivery Info if applicable */}
        {order.delivery_type === 'delivery' && order.delivery_address && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-1626">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-1627">
              <MapPin className="w-5 h-5 text-gray-400" data-id="element-1628" />
              Dirección de Entrega
            </h3>
            <p className="text-gray-600" data-id="element-1629">{order.delivery_address}</p>
            {order.notes && <div className="mt-4 p-3 bg-amber-50 rounded-xl text-sm text-amber-800 border border-amber-100" data-id="element-1630">
                <span className="font-semibold" data-id="element-1631">Notas:</span> {order.notes}
              </div>}
          </div>}

        <div className="text-center text-gray-500 text-sm" data-id="element-1632">
          <p data-id="element-1633">Esta página se actualiza automáticamente.</p>
          <p className="mt-2" data-id="element-1634">
            ¿Necesitas ayuda?{' '}
            <a href="#" className="text-brand-amber hover:underline font-medium" data-id="element-1635">
              Contacta al soporte
            </a>
          </p>
        </div>
      </main>

      <Footer data-id="element-1636" />
    </div>;
}